/**
 * Legacy Course Word Configuration Management (for backward compatibility)
 */import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/firebase';
import {
  WordTrainingData,
  CreateWordTrainingDataInput,
  FingerCombinationMap,
  WordTrainingContent,
  AdminWordSelection,
  CourseTrainingConfiguration,
  AdminTrainingSelection,
  ComplexityMode
} from '@/types/wordTraining';
import {
  mapFingersToKey,
  validateFingerCombination
} from '@/utils/fingerCombinationMapper';
import { FingerType } from '@/types';

/**
 * Word Training Service
 * 
 * Comprehensive service for managing word training data in Firestore.
 * Handles CRUD operations for finger combination word mappings.
 */

// Collection names
const WORD_TRAINING_COLLECTION = 'wordTraining';
const COURSE_TRAINING_CONFIG_COLLECTION = 'courseTrainingConfigurations'; // NEW: Unified collection

/**
 * Gets word training data by finger combination
 */
export const getWordsByFingerCombination = async (fingers: FingerType[], hand: 'left' | 'right'): Promise<WordTrainingData | null> => {
  try {
    if (!fingers || fingers.length === 0) {
      console.warn('No fingers provided for word training lookup');
      return null;
    }

    const fingerKey = `${mapFingersToKey(fingers)}_${hand}`;
    console.log(`🔍 Looking up words for finger combination: ${fingerKey}`);

    const docRef = doc(db, WORD_TRAINING_COLLECTION, fingerKey);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as WordTrainingData;
      console.log(`✅ Found word training data for ${fingerKey}:`, {
        wordsCount: Object.values(data.words).flat().length,
        phrasesCount: data.phrases.length
      });
      return data;
    } else {
      console.log(`⚠️ No word training data found for finger combination: ${fingerKey}`);
      return null;
    }
  } catch (error) {
    console.error('❌ Error fetching words by finger combination:', error);
    throw error;
  }
};

/**
 * Creates or updates word training data for a finger combination
 */
export const createWordTrainingData = async (
  fingerKey: string,
  input: CreateWordTrainingDataInput,
  hand: 'left' | 'right'
): Promise<void> => {
  try {
    console.log(`📝 Creating word training data for ${fingerKey}`);

    // Validate finger combination
    if (!validateFingerCombination(input.fingerCombination)) {
      throw new Error(`Invalid finger combination: ${input.fingerCombination}`);
    }

    // Use explicit hand parameter
    const handValue = hand;

    const wordTrainingData: WordTrainingData = {
      fingerCombination: input.fingerCombination,
      fingerCombinationKey: fingerKey,
      hand: handValue,
      words: {
        length3: input.words.length3 || [],
        length4: input.words.length4 || [],
        length5: input.words.length5 || [],
        length6: input.words.length6 || [],
        length7: input.words.length7 || [],
        length8plus: input.words.length8plus || []
      },
      phrases: input.phrases || [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = doc(db, WORD_TRAINING_COLLECTION, fingerKey);
    await setDoc(docRef, wordTrainingData);

    console.log(`✅ Word training data created for ${fingerKey}`);
  } catch (error) {
    console.error('❌ Error creating word training data:', error);
    throw error;
  }
};

/**
 * Updates existing word training data
 */
export const updateWordTrainingData = async (
  fingerKey: string,
  updates: Partial<CreateWordTrainingDataInput>
): Promise<void> => {
  try {
    console.log(`📝 Updating word training data for ${fingerKey}`);

    const docRef = doc(db, WORD_TRAINING_COLLECTION, fingerKey);
    const updateData: any = {
      updatedAt: serverTimestamp()
    };

    if (updates.words) {
      updateData.words = updates.words;
    }

    if (updates.phrases) {
      updateData.phrases = updates.phrases;
    }

    await updateDoc(docRef, updateData);
    console.log(`✅ Word training data updated for ${fingerKey}`);
  } catch (error) {
    console.error('❌ Error updating word training data:', error);
    throw error;
  }
};

/**
 * Gets all available finger combinations that have word training data
 */
export const getAvailableFingerCombinations = async (): Promise<string[]> => {
  try {
    console.log('🔍 Fetching available finger combinations');

    const querySnapshot = await getDocs(collection(db, WORD_TRAINING_COLLECTION));
    const fingerCombinations: string[] = [];

    querySnapshot.forEach((doc) => {
      fingerCombinations.push(doc.id);
    });

    console.log(`✅ Found ${fingerCombinations.length} finger combinations with word data`);
    return fingerCombinations;
  } catch (error) {
    console.error('❌ Error fetching available finger combinations:', error);
    throw error;
  }
};

/**
 * Gets all word training data as a map
 */
export const getAllWordTrainingData = async (): Promise<FingerCombinationMap> => {
  try {
    console.log('🔍 Fetching all word training data');

    const querySnapshot = await getDocs(collection(db, WORD_TRAINING_COLLECTION));
    const dataMap: FingerCombinationMap = {};

    querySnapshot.forEach((doc) => {
      dataMap[doc.id] = doc.data() as WordTrainingData;
    });

    console.log(`✅ Loaded word training data for ${Object.keys(dataMap).length} finger combinations`);
    return dataMap;
  } catch (error) {
    console.error('❌ Error fetching all word training data:', error);
    throw error;
  }
};

/**
 * Generates training content from word selection - SIMPLIFIED APPROACH
 */
export const generateWordTrainingContent = async (
  fingers: FingerType[],
  hand: 'left' | 'right',
  selection?: AdminWordSelection
): Promise<WordTrainingContent> => {
  try {
    const wordData = await getWordsByFingerCombination(fingers, hand);

    // 🔥 SIMPLIFIED: Handle empty combinations gracefully - don't throw errors
    if (!wordData) {
      console.log(`ℹ️ No word training data found for ${mapFingersToKey(fingers)}_${hand} - returning empty content`);
      return {
        words: [],
        phrases: [],
        fingerCombination: fingers,
        hand,
        totalItems: 0,
        isEmpty: true,  // 🔥 NEW: Flag to indicate empty combination
        message: 'No real words available for this finger combination. Try letter training mode instead!' // 🔥 NEW: Simple user message
      };
    }

    // Check if the combination has any content
    const totalWords = Object.values(wordData.words).flat().length;
    const totalPhrases = wordData.phrases.length;

    if (totalWords === 0 && totalPhrases === 0) {
      console.log(`ℹ️ Empty word data found for ${mapFingersToKey(fingers)}_${hand} - returning empty content with message`);
      return {
        words: [],
        phrases: [],
        fingerCombination: fingers,
        hand,
        totalItems: 0,
        isEmpty: true,
        message: 'No real words available for this finger combination. Try letter training mode instead!'
      };
    }

    let words: string[] = [];
    let phrases: string[] = [];

    if (!selection) {
      // Default: use phrases for word training mode
      phrases = wordData.phrases.slice();
    } else if (selection.mode === 'manual' && selection.manual) {
      words = selection.manual.selectedWords;

      // 🔥 NEW: Check for magic string "all" to use all available phrases for this finger combination
      if (selection.manual.selectedPhrases.length === 1 && selection.manual.selectedPhrases[0].toLowerCase() === 'all') {
        phrases = wordData.phrases.slice();
      } else {
        phrases = selection.manual.selectedPhrases;
      }
    } else if (selection.mode === 'random' && selection.random) {
      // Random selection based on configuration
      const { wordLengths, includePhrases, quantity } = selection.random;

      // Collect words by selected lengths
      wordLengths.forEach(length => {
        switch (length) {
          case 3: words.push(...wordData.words.length3); break;
          case 4: words.push(...wordData.words.length4); break;
          case 5: words.push(...wordData.words.length5); break;
          case 6: words.push(...wordData.words.length6); break;
          case 7: words.push(...wordData.words.length7); break;
          case 8: words.push(...wordData.words.length8plus); break;
        }
      });

      if (includePhrases) {
        phrases.push(...wordData.phrases);
      }

      // Shuffle and limit to quantity
      words = shuffleArray(words).slice(0, Math.floor(quantity * 0.7)); // 70% words
      phrases = shuffleArray(phrases).slice(0, Math.floor(quantity * 0.3)); // 30% phrases
    }

    return {
      words,
      phrases,
      fingerCombination: wordData.fingerCombination,
      hand: wordData.hand,
      totalItems: words.length + phrases.length,
      isEmpty: false
    };
  } catch (error) {
    console.error('❌ Error generating word training content:', error);
    // 🔥 SIMPLIFIED: Return empty content with error message instead of throwing
    return {
      words: [],
      phrases: [],
      fingerCombination: fingers,
      hand,
      totalItems: 0,
      isEmpty: true,
      message: 'Unable to load words for this finger combination. Try letter training mode instead!'
    };
  }
};

/**
 * Deletes word training data for a finger combination
 */
export const deleteWordTrainingData = async (fingerKey: string): Promise<void> => {
  try {
    console.log(`🗑️ Deleting word training data for ${fingerKey}`);

    const docRef = doc(db, WORD_TRAINING_COLLECTION, fingerKey);
    await deleteDoc(docRef);

    console.log(`✅ Word training data deleted for ${fingerKey}`);
  } catch (error) {
    console.error('❌ Error deleting word training data:', error);
    throw error;
  }
};

/**
 * Course Training Configuration Management - UNIFIED FOR ALL TRAINING TYPES
 */

/**
 * Saves course training configuration for any complexity mode
 */
export const saveCourseTrainingConfiguration = async (
  levelNumber: number,
  dayNumber: number,
  roundNumber: number,
  fingerCombination: string[],
  hand: 'left' | 'right',
  complexity: ComplexityMode,
  trainingSelection: AdminTrainingSelection
): Promise<void> => {
  try {
    const configId = `level_${levelNumber}_day_${dayNumber}_round_${roundNumber}`;
    console.log(`📝 Saving course training configuration: ${configId}`);

    const config: CourseTrainingConfiguration = {
      levelNumber,
      dayNumber,
      roundNumber,
      fingerCombination,
      hand,
      complexity,
      trainingSelection,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = doc(db, COURSE_TRAINING_CONFIG_COLLECTION, configId);
    await setDoc(docRef, config);

    console.log(`✅ Course training configuration saved: ${configId}`);
  } catch (error) {
    console.error('❌ Error saving course training configuration:', error);
    throw error;
  }
};

/**
 * Gets course training configuration (NEW UNIFIED VERSION)
 */
export const getCourseTrainingConfiguration = async (
  levelNumber: number,
  dayNumber: number,
  roundNumber: number
): Promise<CourseTrainingConfiguration | null> => {
  try {
    const configId = `level_${levelNumber}_day_${dayNumber}_round_${roundNumber}`;
    const docRef = doc(db, COURSE_TRAINING_CONFIG_COLLECTION, configId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as CourseTrainingConfiguration;
    }

    return null;
  } catch (error) {
    console.error('❌ Error fetching course training configuration:', error);
    throw error;
  }
};

/**
 * Gets all course training configurations
 */
export const getAllCourseTrainingConfigurations = async (): Promise<CourseTrainingConfiguration[]> => {
  try {
    console.log('🔍 Fetching all course training configurations');

    const querySnapshot = await getDocs(collection(db, COURSE_TRAINING_CONFIG_COLLECTION));
    const configurations: CourseTrainingConfiguration[] = [];

    querySnapshot.forEach((doc) => {
      configurations.push(doc.data() as CourseTrainingConfiguration);
    });

    // Sort by level, day then by round
    configurations.sort((a, b) => {
      if (a.levelNumber !== b.levelNumber) {
        return (a.levelNumber || 1) - (b.levelNumber || 1);
      }
      if (a.dayNumber !== b.dayNumber) {
        return a.dayNumber - b.dayNumber;
      }
      return a.roundNumber - b.roundNumber;
    });

    console.log(`✅ Loaded ${configurations.length} course training configurations`);
    return configurations;
  } catch (error) {
    console.error('❌ Error fetching all course training configurations:', error);
    throw error;
  }
};

/**
 * Deletes a course training configuration
 */
export const deleteCourseTrainingConfiguration = async (
  levelNumber: number,
  dayNumber: number,
  roundNumber: number
): Promise<void> => {
  try {
    const configId = `level_${levelNumber}_day_${dayNumber}_round_${roundNumber}`;
    console.log(`🗑️ Deleting course training configuration: ${configId}`);

    const docRef = doc(db, COURSE_TRAINING_CONFIG_COLLECTION, configId);
    await deleteDoc(docRef);

    console.log(`✅ Course training configuration deleted: ${configId}`);
  } catch (error) {
    console.error('❌ Error deleting course training configuration:', error);
    throw error;
  }
};

/**
 * Legacy word configuration functions (for backward compatibility)
 */
export const saveCourseWordConfiguration = saveCourseTrainingConfiguration;
export const getCourseWordConfiguration = getCourseTrainingConfiguration;
export const getAllCourseWordConfigurations = getAllCourseTrainingConfigurations;
export const deleteCourseWordConfiguration = deleteCourseTrainingConfiguration;

/**
 * Utility functions
 */

/**
 * Shuffles an array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Validates word content for appropriateness
 */
export const validateWordContent = (words: string[]): { valid: string[]; invalid: string[] } => {
  const valid: string[] = [];
  const invalid: string[] = [];

  words.forEach(word => {
    // Basic validation: no empty strings, reasonable length, no special characters
    if (word &&
      word.length >= 1 &&
      word.length <= 50 &&
      /^[a-zA-Z\s'-]+$/.test(word)) {
      valid.push(word.trim());
    } else {
      invalid.push(word);
    }
  });

  return { valid, invalid };
};

/**
 * Formats finger combination for display
 */
export const formatFingerCombinationForDisplay = (combination: string[]): string => {
  const fingerNames: Record<string, string> = {
    T: 'Thumb',
    I: 'Index',
    M: 'Middle',
    R: 'Ring',
    P: 'Pinky'
  };

  return combination.map(abbrev => fingerNames[abbrev] || abbrev).join(' + ');
};

/**
 * Gets word training statistics
 */
export const getWordTrainingStatistics = async (): Promise<{
  totalCombinations: number;
  totalWords: number;
  totalPhrases: number;
  averageWordsPerCombination: number;
  averagePhrasesPerCombination: number;
}> => {
  try {
    const allData = await getAllWordTrainingData();
    const combinations = Object.values(allData);

    const totalWords = combinations.reduce((sum, data) =>
      sum + Object.values(data.words).flat().length, 0);

    const totalPhrases = combinations.reduce((sum, data) =>
      sum + data.phrases.length, 0);

    return {
      totalCombinations: combinations.length,
      totalWords,
      totalPhrases,
      averageWordsPerCombination: combinations.length > 0 ? totalWords / combinations.length : 0,
      averagePhrasesPerCombination: combinations.length > 0 ? totalPhrases / combinations.length : 0
    };
  } catch (error) {
    console.error('❌ Error getting word training statistics:', error);
    throw error;
  }
};
